import { Injectable } from '@angular/core';
import { fetchUserAttributes, signUp } from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject } from 'rxjs';
import jwt_decode from 'jwt-decode';
import { User } from '@reenstudio/app/core/models/user/user';
import { UserToken } from '../models/user/user-token';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { NGXLogger } from 'ngx-logger';
import {
  CognitoIdentityClient,
  ListIdentityPoolsCommand,
} from '@aws-sdk/client-cognito-identity';
import { oauthConfig } from '@reenstudio/app/core/config/oauth-config';
import { CognitoService } from './cognito-service';
import { UserRole } from '../enum/user-role';
import { AppService } from './app.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _currentUser: BehaviorSubject<User | null>;

  private client: CognitoIdentityClient;
  region = oauthConfig.cognito.region;
  userPoolId = oauthConfig.cognito.userPoolId;

  constructor(
    private router: Router,
    private storageService: StorageService,
    private permissionService: NgxPermissionsService,
    private cognitoService: CognitoService,
    private snackBar: SnackbarService,
    private logger: NGXLogger,
    private appService: AppService
  ) {
    this._currentUser = new BehaviorSubject<User | null>(
      this.getUserFromToken()
    );
    this.client = new CognitoIdentityClient({ region: this.region });
  }

  get currentUser(): BehaviorSubject<User | null> {
    const currentUser = new BehaviorSubject<User | null>(
      this.getUserFromToken()
    );
    return currentUser;
  }

  /** @description Registrazione utente */
  async signUp(name: string, username: string, password: string) {
    await this.cognitoService
      .handleSignUp({
        username: username,
        password: password,
        email: username,
      })
      .then(() => {
        this.storageService.setEmail(username);
        this.snackBar.success(
          'Registrazione eseguita. Controlla la tua casella di posta.'
        );
      })
      .catch((error: any) => {
        this.snackBar.error(error);
      });
  }

  /** @description Verifico utente dopo la registrazione o l'inserimento a sistema da parte di un admin */
  async verify(username: string, code: string): Promise<boolean> {
    await this.cognitoService
      .handleSignUpConfirmation({
        username: username,
        confirmationCode: code,
      })
      .then((success: any) => {
        this.snackBar.success('Registrazione terminata correttamente.');

        // Associo l'utente a un gruppo
        this.cognitoService.handleUpdateUserAttribute(
          'custom:group',
          UserRole.SITE
        );

        this.storageService.clear();
        this.router.navigate(['/login']);
      })
      .catch((error: any) => {
        console.log(error);
        this.snackBar.error(
          "Si è verificato un errore nell'invio del codice di verifica"
        );
        return false;
      });
    return false;
  }

  /** @description Rimanda il codice di verifica dalla pagina di inserimento codice di verifica */
  resendVerify(username: string) {
    // Auth.resendSignUp(username)
    //   .then(() => {
    //     this.snackBar.success('Codice di verifica inviato con successo');
    //   })
    //   .catch((error: any) => {
    //     console.log(error);
    //     this.snackBar.success(
    //       "Si è verificato un errore nell'invio del codice di verifica. Riprova più tardi"
    //     );
    //   });
  }

  isAuthenticated(): boolean {
    const token = this.storageService.getToken();

    if (token) {
      return !this.isTokenExpired(token);
    }

    return false;
  }

  isTokenExpired(token: string) {
    return this.decodeToken(token).exp < Date.now() / 1000;
  }

  async refreshToken() {
    try {
      const tokens = await this.cognitoService.refreshToken();

      this.logger.debug('Token refreshed', tokens);
      if (tokens.idToken) {
        this.storageService.setToken(tokens.idToken);
      }
      return tokens;
      // Il token è stato automaticamente rinnovato
    } catch (error) {
      this.logger.debug('Refresh token error', error);
      this.signOut();
      return false;
    }
  }

  setToken(token: string) {
    this.storageService.setToken(token);
    this.loadUser();
  }

  loadUser() {
    if (!this._currentUser.value) {
      this._currentUser.next(this.getUserFromToken());
    }
  }

  /**
   * @description Decodifica token
   * @private
   */
  private getUserFromToken(): User | null {
    const token = this.storageService.getToken();
    const userId = this.storageService.getUserId();
    const name = this.storageService.getName();
    const email = this.storageService.getEmail();
    const level = this.storageService.getLevel();

    if (!token || !userId || !name || !email || !level) {
      return null;
    }

    const tokenDecoded = this.decodeToken(token);

    const user = new User(userId, name, email, level);

    this.permissionService.loadPermissions([user.getLevel()]);

    return user;
  }

  /**
   * @description Decodifica e mappamento token
   * @param token
   * @private
   */
  private decodeToken(token: string): UserToken {
    return <UserToken>jwt_decode(token);
  }

  /**
   * Procedura di password dimenticata
   * @param username
   */
  forgot(username: string) {
    this.cognitoService.handleResetPassword(username).catch((error: any) => {
      if (error.name === 'LimitExceededException') {
        this.snackBar.error(
          'Superato limite tentativi, si prega di riprovare più tardi.'
        );
      }
    });
  }

  forgotConfirm(confirmData: any) {
    this.cognitoService
      .handleConfirmResetPassword({
        username: confirmData.username,
        confirmationCode: confirmData.code,
        newPassword: confirmData.password,
      })
      .then(() => {
        this.snackBar.success('Password modificata correttamente');
        this.router.navigate(['/login']);
      })
      .catch((error: any) => {
        console.log(error);
        this.snackBar.error('Si è verificato un errore');
      });
  }

  resendForgot(username: string) {
    this.cognitoService
      .handleResetPassword(username)
      .then(() => {
        this.snackBar.success('Codice di verifica inviato con successo');
      })
      .catch((error: any) => {
        console.log(error.name);
        if (error.name === 'LimitExceededException') {
          this.snackBar.error(
            'Superato limite tentativi, si prega di riprovare più tardi.'
          );
        } else {
          this.snackBar.error(
            "Si è verificato un errore nell'invio del codice di verifica. Riprova più tardi"
          );
        }
      });
  }

  async listUsers(): Promise<any> {
    const command = new ListIdentityPoolsCommand({
      MaxResults: Number('int'),
    });

    try {
      const response = await this.client.send(command);
      return response.IdentityPools;
    } catch (error) {
      console.error('Errore nel recupero degli utenti:', error);
      throw error;
    }
  }

  signOut(): void {
    this.cognitoService.handleSignOut().then(() => {
      localStorage.clear();
      this.router.navigate(['/login']);
    });
  }

  getUserAttributes(): Promise<any> {
    return new Promise((resolve, reject) => {
      fetchUserAttributes()
        .then((userAttributes) => {
          resolve(userAttributes);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
